/**
 * ProductRowsTable - Main products table component
 *
 * Renders a professional-looking table of products/services with:
 * - Column headers
 * - Draggable rows (when in draft mode)
 * - Clean empty state
 *
 * Note: The parent container handles scrolling to avoid nested scroll issues
 * with @hello-pangea/dnd. The Droppable container should not have its own scroll.
 */

import { Box } from '@mui/material'
import React from 'react'
import ProductRow from '../ContractDialog/ProductRow'
import ProductTableEmpty from './ProductTableEmpty'
import { ProductTableContainer } from './ProductTableStyles'

interface ProductRowsTableProps {
  deal: any
  productRows: any[]
  onDragEnd: (result: any) => void
  onProductChange: (index: number, field: string, value: any, extra?: any) => void
  validationErrors: any
  removeRow: (sk: string) => void
  containerHeight?: number | string
  listRef?: React.RefObject<HTMLDivElement>
  customByServiceKey?: Map<string, { price: number; margin: number }>
  editableFields?: Set<string>
}

const ProductRowsTable = ({
  deal,
  productRows,
  onDragEnd,
  onProductChange,
  validationErrors,
  removeRow,
  containerHeight,
  listRef,
  customByServiceKey,
  editableFields,
}: ProductRowsTableProps) => {
  const hasProducts = productRows && Array.isArray(productRows) && productRows.length > 0

  const dealType = deal?.type || ''

  return (
    <ProductTableContainer
      ref={listRef}
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        maxHeight: containerHeight,
      }}
    >
      {/* Product Rows */}
      {hasProducts ? (
        <Box sx={{ pb: 0.5 }}>
          {productRows.map((row, index) => {
            // Use SK as unique key, fallback to rowId, then index
            const uniqueKey = row.SK || row.rowId || `row-${index}`
            const draggableId = row.SK || String(row.rowId) || `draggable-${index}`

            return (
              <React.Fragment key={uniqueKey}>
                <ProductRow
                  row={row}
                  index={index}
                  handleProductChange={onProductChange}
                  handleRemoveRow={removeRow}
                  validationErrors={validationErrors}
                  type={dealType}
                  isDraft={deal?.isDraft}
                  customByServiceKey={customByServiceKey}
                  editableFields={editableFields}
                />
              </React.Fragment>
            )
          })}
        </Box>
      ) : (
        <ProductTableEmpty isDraft={deal?.isDraft} />
      )}
    </ProductTableContainer>
  )
}

export default ProductRowsTable
